import { useId, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { roleHasPermission, SYSTEM_ROLES, type SystemRole } from "@ortura/shared";
import { useApi } from "../api/api-context";
import { createInvitation, membersQuery } from "../api/platform";
import { ErrorMessage } from "../components/error-message";
import { Button } from "../components/ui/button";
import { Card, CardTitle } from "../components/ui/card";
import { FieldHint, Input, Label, Select } from "../components/ui/form";
import { useTenant } from "./tenant-layout";

// Mitgliederliste und Einladungslink (Entscheidung 30). Ob jemand einladen darf,
// entscheidet die API (requirePermission + DB-Funktion). Die Oberfläche blendet
// den Bereich nur aus, damit niemand auf einen sicheren Fehler läuft.
export function MembersPage() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const tenant = useTenant();
  const members = useQuery(membersQuery(api, tenant.tenantId));
  const canInvite = roleHasPermission(tenant.role, "members:invite");

  // Datum in der aktiven Sprache (Entscheidung 36: Formate über Intl).
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <h1 className="text-2xl text-ink">{t("members.title")}</h1>

      <Card className="overflow-x-auto p-0">
        {members.isPending ? (
          <p className="p-6 text-sm text-ink-muted">{t("app.loading")}</p>
        ) : members.isError ? (
          <div className="p-6">
            <ErrorMessage error={members.error} />
          </div>
        ) : members.data.members.length === 0 ? (
          <p className="p-6 text-sm text-ink-muted">{t("members.empty")}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line text-ink-muted">
              <tr>
                <th className="px-6 py-3 font-medium">{t("members.name")}</th>
                <th className="px-6 py-3 font-medium">{t("members.email")}</th>
                <th className="px-6 py-3 font-medium">{t("members.role")}</th>
                <th className="px-6 py-3 font-medium">{t("members.joined")}</th>
              </tr>
            </thead>
            <tbody>
              {members.data.members.map((m) => (
                <tr key={m.userId} className="border-b border-line last:border-0">
                  <td className="px-6 py-3 text-ink">{m.name ?? "–"}</td>
                  <td className="px-6 py-3 text-ink-muted">{m.email}</td>
                  <td className="px-6 py-3 text-ink">{t(`roles.${m.role}`)}</td>
                  <td className="px-6 py-3 text-ink-muted">
                    {dateFormat.format(new Date(m.joinedAt))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {canInvite && <InviteCard tenantId={tenant.tenantId} />}
    </div>
  );
}

function InviteCard({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const roleId = useId();
  const linkId = useId();
  const [role, setRole] = useState<SystemRole>("viewer");
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () => createInvitation(api, tenantId, { role }),
    onSuccess: () => setCopied(false),
  });

  // Der Link wird hier gebaut; das Token steht nach "#" (siehe invite-page.tsx).
  const link = mutation.data ? `${window.location.origin}/invite#${mutation.data.token}` : null;

  async function copy(text: string): Promise<void> {
    await navigator.clipboard.writeText(text);
    setCopied(true);
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <CardTitle>{t("members.inviteTitle")}</CardTitle>
        <FieldHint>{t("members.inviteIntro")}</FieldHint>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor={roleId}>{t("members.inviteRole")}</Label>
          <Select id={roleId} value={role} onChange={(e) => setRole(e.target.value as SystemRole)}>
            {SYSTEM_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </Select>
        </div>
        <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {t("members.inviteSubmit")}
        </Button>
      </div>

      {mutation.error && <ErrorMessage error={mutation.error} />}

      {link && (
        <div className="flex flex-col gap-2">
          <Label htmlFor={linkId}>{t("members.inviteLink")}</Label>
          <div className="flex gap-2">
            <Input id={linkId} readOnly value={link} onFocus={(e) => e.target.select()} />
            <Button variant="secondary" onClick={() => void copy(link)}>
              {copied ? t("members.copied") : t("members.copy")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
